import { toast } from "@ui/molecules/Toasts/toast";
import { promptWithoutVideoThumbnails } from "@ui/organisms/QuestionBox/promptMentionSerialization";
import { getPreferredLocale } from "@utils/locale";
import { batch, createSignal, onCleanup, onMount } from "solid-js";
import { createStore, produce } from "solid-js/store";
import {
	askFollowUpQuestion,
	askFollowUpQuestionById,
	getRatings,
} from "@/client";
import {
	hasNoCreditsLeft,
	useMeteringBalance,
} from "~/modules/shared/utils/useMeteringBalance";
import type {
	QuestionBoxHandle,
	QuestionEffort,
} from "../../gvtui/organisms/QuestionBox/QuestionBox.tsx";
import { type AskFailure, askFailure } from "../askFailure.ts";
import type { QueryContext } from "../components/FocusBanner.tsx";
import { QUESTION_ANSWER_COMPLETE_EVENT } from "../constants";
import {
	type ChatPanelUiState,
	createInitialUiState,
} from "./chatPanelState.ts";
import type { ExecutionImageResultContent } from "./chatStreamEvents.ts";
import {
	appendExecutionImageResult,
	appendQuestion,
	type ChatStreamArtifacts,
	type ChatStreamEffect,
	ensureInteraction,
	getCurrentInteractionIndex,
	parseChatStreamEvent,
	reduceChatStreamEvent,
	renderedArtifactIds,
} from "./chatStreamReducer.ts";
import { emitChatCardToolbarRatingUpdated } from "./components/ChatCardToolbar.tsx";
import { writeLiveThinkingDetailsOpen } from "./liveThinkingPreference.ts";
import {
	type CollectedMentions,
	createMentionCollector,
} from "./mentionCollector.ts";
import {
	hideVideoEvidence,
	scrollVideoEvidenceOffView,
	showVideoEvidence,
} from "./videoEvidence.ts";

export type FollowUpResult = { ok: true; interactionId?: string } | AskFailure;

export type ChatPanelController = {
	askQuestion: (promptText: string) => void;
	/** Asks without showing anything on the page, reporting how it went instead. */
	askFollowUp: (
		promptText: string,
		effort?: QuestionEffort,
	) => Promise<FollowUpResult>;
	registerForChatEvents: (events: string[]) => void;
	scrollVideoEvidenceOffView: (
		interactionId: string,
		offset?: number,
	) => boolean;
	hideVideoEvidence: (interactionId: string) => void;
	showVideoEvidence: (interactionId: string) => void;
	refreshConversation: () => void;
	/**
	 * Refreshes from another conversation that continues this one's
	 * interactions, such as a fork of it, and keeps loading from there.
	 */
	continueConversation: (conversationUrl: string) => void;
	setReasoningEventsOpen: (interactionIndex: number, open: boolean) => void;
	setReasoningToggleOpen: (interactionIndex: number, open: boolean) => void;
	clearQuestionScrollTarget: () => void;
	getMentions: () => CollectedMentions;
};

type ControllerOptions = {
	questionId: string;
	projectId: string | null;
	completionType: "social_account" | "influencer";
	includeRecentCompletions?: boolean;
	onQueryContext?: (queryContext: QueryContext | undefined) => void;
	onSuppressFocusEvents?: (suppress: boolean) => void;
	/**
	 * Replaces the question endpoint the conversation is loaded from. Fetched
	 * as given, except that a refresh adds `latest_interaction_id`, so the
	 * endpoint need only send what came after it.
	 */
	conversationUrl?: string;
	/** Ratings are owner-scoped, so a viewer who doesn't own the question can't load them. Defaults to true. */
	loadRatings?: boolean;
	/** Called when loading the conversation finishes, with the server's message if it failed. */
	onConversationSettled?: (error?: string) => void;
	/** Social account public ids whose avatars the thinking image ticker never shows. */
	excludedTickerSocialAccountIds?: readonly string[];
};

export function useChatPanelController(options: ControllerOptions) {
	const [state, setState] = createStore<ChatPanelUiState>(
		createInitialUiState(),
	);
	const [registeredChatEvents, setRegisteredChatEvents] = createSignal<
		string[]
	>([]);
	const [questionBoxHandle, setQuestionBoxHandle] = createSignal<
		QuestionBoxHandle | undefined
	>();

	const queryContexts = new Map<string, QueryContext>();
	const artifacts: ChatStreamArtifacts = new Map();
	// Mentions a host app can read back on demand, collected off the stream
	// itself rather than off the rendered UI (see mentionCollector).
	const mentionCollector = createMentionCollector();
	let activeQueryContextId: string | undefined;
	let queryContextIntersectionObserver: IntersectionObserver | undefined;
	let queryContextMutationObserver: MutationObserver | undefined;
	let transcriptElement: HTMLElement | undefined;
	let executionImageResultsInterval: ReturnType<typeof setInterval> | undefined;
	const executionImageResults: {
		result: ExecutionImageResultContent;
		dispatchedToApp: boolean;
	}[] = [];
	let streamAborted = false;

	// Applied to this controller's own state rather than through the document
	// events, which every controller on the page would otherwise act on.
	const enableAsk = (enabled = true) => {
		setState("replyDisabled", !enabled);
		setState("isReasoning", !enabled);
		if (enabled) {
			questionBoxHandle()?.focus();
		} else {
			questionBoxHandle()?.blur();
		}
		document.dispatchEvent(
			new CustomEvent(enabled ? "enableask" : "disableask"),
		);
	};

	const handleRateInteraction = (content: {
		artifact_id?: string;
		rating?: number;
		reason?: string | null;
	}) => {
		if (!content.artifact_id) {
			return;
		}
		emitChatCardToolbarRatingUpdated({
			artifactId: content.artifact_id,
			rating: Number(content.rating) === 1 ? 1 : -1,
			reason: content.reason,
		});
	};

	const queueExecutionImageResult = (
		result: ExecutionImageResultContent,
		dispatchedToApp: boolean,
	) => {
		executionImageResults.push({ result, dispatchedToApp });
		if (!executionImageResultsInterval) {
			executionImageResultsInterval = setInterval(() => {
				const item = executionImageResults.shift();
				if (item) {
					setState(
						produce((s: ChatPanelUiState) =>
							appendExecutionImageResult(
								s,
								item.result,
								item.dispatchedToApp,
								options.excludedTickerSocialAccountIds,
							),
						),
					);
				}
			}, 400);
		}
	};

	const runEffect = (effect: ChatStreamEffect) => {
		switch (effect.kind) {
			case "dispatch_chat_event":
				document.dispatchEvent(
					new CustomEvent("chatevent", { detail: effect.detail }),
				);
				break;
			case "record_mention":
				mentionCollector.record(effect.eventType, effect.content);
				break;
			case "end_interaction":
				document.dispatchEvent(new Event("end_interaction"));
				break;
			case "enable_ask":
				enableAsk();
				break;
			case "answer_complete":
				document.dispatchEvent(
					new CustomEvent(QUESTION_ANSWER_COMPLETE_EVENT, {
						detail: { questionId: options.questionId },
					}),
				);
				break;
			case "alert":
				toast.info({
					title: effect.message,
				});
				break;
			case "query_context":
				queryContexts.set(effect.contextId, effect.queryContext);
				if (effect.live) {
					options.onQueryContext?.(effect.queryContext);
				}
				break;
			case "queue_execution_image_result":
				queueExecutionImageResult(effect.result, effect.dispatchedToApp);
				break;
			case "unknown_event":
				console.log(`Unknown event "${effect.eventType}" not handled.`);
				break;
		}
	};

	const handleEvent = (eventJSON: string) => {
		const event = parseChatStreamEvent(eventJSON);
		if (!event) {
			return;
		}
		const context = {
			registeredEvents: registeredChatEvents(),
			now: Date.now(),
			artifacts,
			excludedTickerSocialAccountIds: options.excludedTickerSocialAccountIds,
		};
		let effects: ChatStreamEffect[] = [];
		setState(
			produce((s: ChatPanelUiState) => {
				effects = reduceChatStreamEvent(s, event, context);
			}),
		);
		for (const effect of effects) {
			runEffect(effect);
		}
	};

	const streamResponse = async (response: Response) => {
		const reader = response.body?.getReader();
		if (!reader) {
			return;
		}
		const decoder = new TextDecoder("utf-8");
		let buffer = "";

		while (!streamAborted) {
			const { value, done } = await reader.read();
			if (done) {
				break;
			}
			buffer += decoder.decode(value, { stream: true });
			const lines = buffer.split("\n");
			buffer = lines.pop() ?? "";
			// A stored conversation arrives in large chunks of events; render each
			// chunk once instead of once per token.
			batch(() => {
				for (const line of lines) {
					handleEvent(line);
				}
			});
		}
		enableAsk();
	};

	let customConversationUrl = options.conversationUrl;
	const conversationUrl = (previousInteractionID?: string | null) => {
		if (customConversationUrl) {
			return previousInteractionID
				? `${customConversationUrl}?${new URLSearchParams({ latest_interaction_id: previousInteractionID })}`
				: customConversationUrl;
		}
		const baseUrl = options.projectId
			? `/api/projects/${options.projectId}/questions/${options.questionId}`
			: `/api/questions/${options.questionId}`;

		const params = new URLSearchParams({ frame_image_asset_type: "avif" });
		if (previousInteractionID) {
			params.set("latest_interaction_id", previousInteractionID);
		}
		return `${baseUrl}?${params.toString()}`;
	};

	/** The server's own explanation for a failed response, when it gave one. */
	const failureMessage = async (response: Response) => {
		try {
			const body = await response.json();
			if (typeof body?.message === "string" && body.message !== "") {
				return body.message as string;
			}
		} catch {
			// Not JSON, or no body.
		}
		return `Unexpected status ${response.status}`;
	};

	/**
	 * `reopenOnFailure` is for a refresh, which closes the reply box before it
	 * asks: a failed one has no stream to reopen it when it ends.
	 */
	const loadConversation = async (
		previousInteractionID?: string | null,
		reopenOnFailure = false,
	) => {
		let response: Response;
		try {
			response = await fetch(conversationUrl(previousInteractionID), {
				method: "GET",
				headers: {
					Accept: "application/json",
					"Content-Type": "application/json",
				},
			});
		} catch (e) {
			console.error("Failed to fetch stream:", e);
			if (reopenOnFailure) {
				enableAsk();
			}
			options.onConversationSettled?.("Network error");
			return;
		}

		if (!response.ok) {
			console.error("Failed to fetch stream:", response.statusText);
			if (reopenOnFailure) {
				enableAsk();
			}
			options.onConversationSettled?.(await failureMessage(response));
			return;
		}

		await streamResponse(response);
		options.onConversationSettled?.();
		if (options.loadRatings !== false) {
			await fetchRatings();
		}
	};

	/**
	 * Resolves once the server has taken or refused the follow-up; its answer
	 * goes on streaming into the conversation after that.
	 */
	const sendFollowUp = async (
		promptText: string,
		latestInteractionID?: string,
		effort?: QuestionEffort,
	): Promise<FollowUpResult> => {
		if (hasNoCreditsLeft()) {
			return { ok: false, reason: "insufficient_credits" };
		}

		enableAsk(false);

		const followUpBody = {
			prompt: promptWithoutVideoThumbnails(promptText),
			latest_interaction_id:
				latestInteractionID ?? state.latestInteractionId ?? undefined,
			preferred_locale: getPreferredLocale() ?? null,
			effort,
		};
		const followUpQuery = { frame_image_asset_type: "avif" as const };
		const followUpRequest = options.projectId
			? askFollowUpQuestion({
					path: {
						project_id: options.projectId,
						question_id: options.questionId,
					},
					query: followUpQuery,
					body: followUpBody,
					parseAs: "stream",
				})
			: askFollowUpQuestionById({
					path: { question_id: options.questionId },
					query: followUpQuery,
					body: followUpBody,
					parseAs: "stream",
				});

		const { error, response } = await followUpRequest;

		if (!response?.ok) {
			enableAsk(true);
			console.error(
				"Failed to fetch stream:",
				response?.statusText ?? "No response",
			);
			return askFailure(error, response);
		}

		void streamResponse(response);
		return {
			ok: true,
			interactionId: response.headers.get("X-Interaction-Id") ?? undefined,
		};
	};

	const askQuestion = async (
		promptText: string,
		latestInteractionID?: string,
		effort?: QuestionEffort,
	) => {
		const result = await sendFollowUp(promptText, latestInteractionID, effort);
		if (result.ok === false && result.status === 402) {
			toast.error({
				title: "You're out of question credits",
				description:
					"Top up or upgrade your plan to keep asking, or wait for your next reset.",
				store: false,
			});
			void useMeteringBalance().refetch();
		}
	};

	const fetchRatings = async () => {
		const artifactIds = renderedArtifactIds(state, artifacts);
		if (artifactIds.length === 0) {
			return;
		}
		const ratings = await getRatings({
			path: { question_id: options.questionId },
			query: { artifact_ids: artifactIds.join(",") },
		});
		if (ratings.error) {
			console.error("Failed to fetch ratings:", ratings.error);
			return;
		}
		for (const rating of ratings.data?.ratings ?? []) {
			handleRateInteraction(rating);
		}
	};

	const handleQuestionSubmit = (
		promptText: string,
		effort?: QuestionEffort,
	) => {
		if (promptText.length === 0 || hasNoCreditsLeft()) {
			return;
		}
		const latestInteractionID = state.latestInteractionId;
		setState(
			produce((s: ChatPanelUiState) => {
				ensureInteraction(s, undefined);
				appendQuestion(s, promptText);
			}),
		);
		const interaction =
			state.interactions[getCurrentInteractionIndex(state.interactions)];
		if (interaction) {
			setState("scrollToInteractionIndex", interaction.index);
		}
		void askQuestion(promptText, latestInteractionID, effort);
	};

	const removeEphemeralBlocks = () => {
		setState(
			produce((s) => {
				const idx = getCurrentInteractionIndex(s.interactions);
				const interaction = s.interactions[idx];
				if (interaction) {
					interaction.ephemeralNotifications = [];
				}
			}),
		);
	};

	const refreshConversation = () => {
		setState("scrollToInteractionIndex", state.nextInteractionIndex);
		enableAsk(false);
		void loadConversation(state.latestInteractionId, true);
		removeEphemeralBlocks();
	};

	const continueConversation = (url: string) => {
		customConversationUrl = url;
		refreshConversation();
	};

	const checkQueryContextCoverage = (el: HTMLElement) => {
		const contextId = el.dataset.queryContextId;
		if (!contextId || contextId === activeQueryContextId) {
			return;
		}
		const rect = el.getBoundingClientRect();
		const visibleTop = Math.max(rect.top, 0);
		const visibleBottom = Math.min(rect.bottom, window.innerHeight);
		const visibleHeight = Math.max(0, visibleBottom - visibleTop);
		if (visibleHeight > window.innerHeight / 2) {
			activeQueryContextId = contextId;
			const queryContext = queryContexts.get(contextId);
			if (queryContext) {
				options.onQueryContext?.(queryContext);
			}
		}
	};

	const setupQueryContextObservers = (transcriptEl: HTMLElement) => {
		transcriptElement = transcriptEl;
		queryContextIntersectionObserver = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					checkQueryContextCoverage(entry.target as HTMLElement);
				}
			},
			{
				threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
			},
		);

		queryContextMutationObserver = new MutationObserver((mutations) => {
			for (const mutation of mutations) {
				for (const node of mutation.addedNodes) {
					if (!(node instanceof HTMLElement)) {
						continue;
					}
					if (node.dataset.queryContextId) {
						queryContextIntersectionObserver?.observe(node);
					}
					for (const child of node.querySelectorAll<HTMLElement>(
						"[data-query-context-id]",
					)) {
						queryContextIntersectionObserver?.observe(child);
					}
				}
			}
		});

		const onResize = () => {
			if (!transcriptElement) {
				return;
			}
			for (const el of transcriptElement.querySelectorAll<HTMLElement>(
				"[data-query-context-id]",
			)) {
				checkQueryContextCoverage(el);
			}
		};

		window.addEventListener("resize", onResize);
		queryContextMutationObserver.observe(transcriptEl, {
			childList: true,
			subtree: true,
		});

		onCleanup(() => {
			window.removeEventListener("resize", onResize);
			queryContextIntersectionObserver?.disconnect();
			queryContextMutationObserver?.disconnect();
		});
	};

	const setReasoningEventsOpen = (interactionIndex: number, open: boolean) => {
		setState(
			produce((s) => {
				const interaction = s.interactions.find(
					(i) => i.index === interactionIndex,
				);
				if (interaction?.answer) {
					interaction.answer.reasoningEventsOpen = open;
					interaction.answer.reasoningToggleOpen = open;
					if (!s.renderingHistory) {
						writeLiveThinkingDetailsOpen(open);
					}
					if (open) {
						for (const plan of interaction.answer.plans) {
							plan.expired = false;
						}
					}
				}
			}),
		);
	};

	const setReasoningToggleOpen = (interactionIndex: number, open: boolean) => {
		setState(
			produce((s) => {
				const interaction = s.interactions.find(
					(i) => i.index === interactionIndex,
				);
				if (interaction?.answer) {
					interaction.answer.reasoningToggleOpen = open;
					interaction.answer.reasoningEventsOpen = open;
					if (!s.renderingHistory) {
						writeLiveThinkingDetailsOpen(open);
					}
					if (open) {
						for (const plan of interaction.answer.plans) {
							plan.expired = false;
						}
					}
				}
			}),
		);
	};

	const clearQuestionScrollTarget = () => {
		setState("scrollToInteractionIndex", undefined);
	};

	const controller: ChatPanelController = {
		askQuestion: (promptText) => {
			void askQuestion(promptText);
		},
		askFollowUp: (promptText, effort) =>
			state.replyDisabled
				? Promise.resolve({ ok: false, reason: "answer_in_progress" })
				: sendFollowUp(promptText, undefined, effort),
		registerForChatEvents: setRegisteredChatEvents,
		scrollVideoEvidenceOffView,
		hideVideoEvidence,
		showVideoEvidence,
		refreshConversation,
		continueConversation,
		setReasoningEventsOpen,
		setReasoningToggleOpen,
		clearQuestionScrollTarget,
		getMentions: mentionCollector.getMentions,
	};

	onMount(() => {
		enableAsk(false);
		void loadConversation();

		onCleanup(() => {
			streamAborted = true;
			if (executionImageResultsInterval) {
				clearInterval(executionImageResultsInterval);
			}
		});
	});

	return {
		state,
		controller,
		handleQuestionSubmit,
		setQuestionBoxHandle,
		setupQueryContextObservers,
	};
}

export type ChatPanelControllerReturn = ReturnType<
	typeof useChatPanelController
>;
